import { ILocation, ILocationVehicleCount, LocationType } from "../models/Location";

/**
 * How many yard sections get a headline tile of their own on the Production Dashboard's TV Line
 * view. A customer setting so a site with a wide screen (or few sections) can show them all; the
 * Total tile still sums every section whatever this is set to.
 */
export const TV_LOCATION_TILES_SETTING_KEY = "ProductionDashboard:TvLocationTiles";
export const DEFAULT_TV_LOCATION_TILES = 6;
export const MAX_TV_LOCATION_TILES = 20;

export function parseTvLocationTiles(value: string | null | undefined): number {
  if (value == null || value.trim() === "") return DEFAULT_TV_LOCATION_TILES;
  const parsed = Number(value);
  if (!Number.isInteger(parsed) || parsed < 0) return DEFAULT_TV_LOCATION_TILES;
  return Math.min(parsed, MAX_TV_LOCATION_TILES);
}

export interface IRackingSummary {
  locationCount: number;
  capacity: number;
  used: number;
  available: number;
}

// Racking can sit inside a yard section or outside any of them, so it is summed from the flat
// location list rather than the section availabilities. Available never goes below zero - an
// overfilled rack shows as full, not as eating into the free space of the others.
export function summariseRacking(
  locations: ILocation[],
  vehicleCounts: ILocationVehicleCount[]
): IRackingSummary {
  const countById = new Map(vehicleCounts.map((c) => [c.locationId, c.vehicleCount]));

  return locations
    .filter((l) => l.type === LocationType.Racking)
    .reduce<IRackingSummary>(
      (summary, l) => {
        const capacity = l.capacity ?? 0;
        const used = countById.get(l.id) ?? 0;
        return {
          locationCount: summary.locationCount + 1,
          capacity: summary.capacity + capacity,
          used: summary.used + used,
          available: summary.available + Math.max(0, capacity - used),
        };
      },
      { locationCount: 0, capacity: 0, used: 0, available: 0 }
    );
}
